import "./App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { UpdateNotice } from "./components/UpdateNotice";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AVVISI_KEYS, PermProvider, RequirePerm, usePerm } from "./context/PermContext";
import AdminProfiles from "./pages/AdminProfiles";
import AdminImport from "./pages/AdminImport";
import Luoghi from "./pages/Luoghi";
import Layout from "./components/Layout";
import { Loading } from "./components/common";
import { isAdmin } from "./lib/roles";
import Login from "./pages/Login";
import { Activate } from "./pages/PasswordRecovery";
import Home from "./pages/Home";
import Turni from "./pages/Turni";
import NewLeave from "./pages/NewLeave";
import Avvisi from "./pages/Avvisi";
import NewAlert from "./pages/NewAlert";
import AlertDetail from "./pages/AlertDetail";
import Report from "./pages/Report";
import NewReport from "./pages/NewReport";
import ReportDetail from "./pages/ReportDetail";
import ShiftReport from "./pages/ShiftReport";
import MyShiftReports from "./pages/MyShiftReports";
import AllShiftReports from "./pages/AllShiftReports";
import Notizie from "./pages/Notizie";
import Profilo from "./pages/Profilo";
import Postit from "./pages/Postit";
import Corsi from "./pages/Corsi";
import Schede from "./pages/Schede";
import NostromoTasks from "./pages/NostromoTasks";
import Admin from "./pages/Admin";
import AdminUsers from "./pages/AdminUsers";
import UserForm from "./pages/UserForm";
import AdminLeaves from "./pages/AdminLeaves";
import AdminComms from "./pages/AdminComms";
import AdminTasks from "./pages/AdminTasks";
import AdminShifts from "./pages/AdminShifts";
import AdminShiftsView from "./pages/AdminShiftsView";
import AdminSite from "./pages/AdminSite";
import PublicLayout from "./public/PublicLayout";
import PublicHome from "./public/PublicHome";
import { PublicContatti, PublicEscursione, PublicEscursioni, PublicEventi, PublicLegal, PublicMeteo, PublicNewsDetail, PublicNotizie, PublicOrmeggio, PublicPorto } from "./public/PublicPages";
import RichiesteOrmeggio from "./pages/RichiesteOrmeggio";
import MioLocale from "./pages/MioLocale";
import Marina from "./pages/Marina";
import MarinaBerths from "./pages/MarinaBerths";
import MarinaAdmin from "./pages/MarinaAdmin";
import Meteo from "./pages/Meteo";
import Parcheggi from "./pages/Parcheggi";
import ParkPass from "./pages/ParkPass";
import Landing from "./pages/Landing";
import Scadenziario from "./pages/Scadenziario";
import { Calendario, MaintExport, ManutenzioniHub, Scadenze } from "./pages/Manutenzioni";
import MaintAnagrafica from "./pages/MaintAnagrafica";
import BeniMobili, { BeneDetail } from "./pages/BeniMobili";
import AdminOvertime from "./pages/AdminOvertime";
import { MeterImport, MeterList, NewReading } from "./pages/Meters";
import { ComunicaVigilanza, VigMine, VigReportDetail, VigReportNew, VigReports } from "./pages/Vigilanza";
import VigStaff from "./pages/VigStaff";
import { AdminCertifications, AdminCourses, AdminMedicalVisits } from "./pages/AdminTraining";
import { Birthdays, HelpAdmin, MissingReports, PeriodicAlerts, QuietHours } from "./pages/AdminMisc";

function Protected({ children, admin }) {
  const { user, loading } = useAuth();
  const perm = usePerm();
  if (loading) return <div className="p-10"><Loading /></div>;
  if (!user) return window.location.pathname === "/" ? <PublicLayout><PublicHome /></PublicLayout> : <Navigate to="/login" replace />;
  if (admin && !(isAdmin(user) && (perm.admin || window.location.pathname === "/admin/profiles"))) return <Navigate to="/" replace />;
  return children;
}

const A = ({ el }) => <Protected admin>{el}</Protected>;
const P = (keys, el) => <RequirePerm keys={keys}>{el}</RequirePerm>;

export default function App() {
  return (
    <AuthProvider>
      <PermProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<Navigate to="/login" replace />} />
          <Route path="/attiva" element={<Activate />} />
          <Route path="/accesso-parcheggio/:id" element={<ParkPass />} />
          <Route path="/anteprima-landing" element={<Landing />} />
          <Route element={<PublicLayout />}>
            <Route path="/il-porto" element={<PublicPorto />} />
            <Route path="/prenota-ormeggio" element={<PublicOrmeggio />} />
            <Route path="/webcam-meteo" element={<PublicMeteo />} />
            <Route path="/notizie" element={<PublicNotizie />} />
            <Route path="/notizie/:id" element={<PublicNewsDetail />} />
            <Route path="/privacy" element={<PublicLegal k="privacy" />} />
            <Route path="/cookie" element={<PublicLegal k="cookie" />} />
            <Route path="/accessibilita" element={<PublicLegal k="accessibilita" />} />
            <Route path="/escursioni" element={<PublicEscursioni />} />
            <Route path="/escursioni/:i" element={<PublicEscursione />} />
            <Route path="/contatti" element={<PublicContatti />} />
            <Route path="/eventi" element={<PublicEventi />} />
          </Route>
          <Route path="/reset-password" element={<Navigate to="/login" replace />} />
          <Route element={<Protected><Layout /></Protected>}>
            <Route index element={<Home />} />
            <Route path="turni" element={P(["turni.view", "turni.ferie", "turni.straordinari"], <Turni />)} />
            <Route path="ferie/new" element={P(["turni.ferie"], <NewLeave />)} />
            <Route path="avvisi" element={P(AVVISI_KEYS, <Avvisi />)} />
            <Route path="alert/new" element={P(["comms.alert"], <NewAlert />)} />
            <Route path="alert/:id" element={<AlertDetail />} />
            <Route path="report" element={<Report />} />
            <Route path="report/new" element={<NewReport />} />
            <Route path="report/view/:id" element={<ReportDetail />} />
            <Route path="report/shift-new" element={P(["report.edit"], <ShiftReport />)} />
            <Route path="report/mine" element={<MyShiftReports />} />
            <Route path="report/turni" element={<AllShiftReports />} />
            <Route path="notizie" element={<Notizie />} />
            <Route path="profilo" element={<Profilo />} />
            <Route path="postit" element={<Postit />} />
            <Route path="corsi" element={P(["corsi.view", "corsi.edit"], <Corsi />)} />
            <Route path="manutenzioni" element={P(["manutenzioni.view", "manutenzioni.interventi"], <ManutenzioniHub />)} />
            <Route path="manutenzioni/interventi" element={P(["manutenzioni.view", "manutenzioni.interventi"], <Schede />)} />
            <Route path="manutenzioni/scadenze" element={<Scadenze />} />
            <Route path="manutenzioni/export" element={<MaintExport />} />
            <Route path="manutenzioni/impianti" element={<MaintAnagrafica />} />
            <Route path="manutenzioni/beni-mobili" element={<BeniMobili />} />
            <Route path="manutenzioni/beni-mobili/:id" element={<BeneDetail />} />
            <Route path="manutenzioni/calendario" element={<Calendario />} />
            <Route path="report/ee" element={P(["letture.ee"], <MeterList kind="ee" />)} />
            <Route path="report/ee/new" element={P(["letture.ee"], <NewReading kind="ee" />)} />
            <Route path="report/immobili" element={P(["letture.gas"], <MeterList kind="immobili" />)} />
            <Route path="report/immobili/new" element={P(["letture.gas"], <NewReading kind="immobili" />)} />
            <Route path="admin/overtime" element={<A el={<AdminOvertime />} />} />
            <Route path="admin/courses" element={<A el={<AdminCourses />} />} />
            <Route path="admin/certifications" element={<A el={<AdminCertifications />} />} />
            <Route path="admin/medical-visits" element={<A el={<AdminMedicalVisits />} />} />
            <Route path="admin/birthdays" element={<A el={<Birthdays />} />} />
            <Route path="admin/missing-reports" element={<A el={<MissingReports />} />} />
            <Route path="admin/periodic-alerts" element={<A el={<PeriodicAlerts />} />} />
            <Route path="admin/quiet-hours" element={<A el={<QuietHours />} />} />
            <Route path="admin/help" element={<A el={<HelpAdmin />} />} />
            <Route path="admin/sito" element={<A el={<AdminSite />} />} />
            <Route path="richieste-ormeggio" element={<RichiesteOrmeggio />} />
            <Route path="mio-locale" element={<MioLocale />} />
            <Route path="vigilanza/new" element={<VigReportNew />} />
            <Route path="vigilanza/mine" element={<VigMine />} />
            <Route path="vigilanza/reports" element={<VigReports />} />
            <Route path="vigilanza/personale" element={<VigStaff />} />
            <Route path="vigilanza/report/:id" element={<VigReportDetail />} />
            <Route path="comunica-vigilanza" element={P(["comms.vig"], <ComunicaVigilanza />)} />
            <Route path="admin/ee-meters" element={<A el={<MeterImport kind="ee" />} />} />
            <Route path="admin/immobili-meters" element={<A el={<MeterImport kind="immobili" />} />} />
            <Route path="meteo" element={<Meteo />} />
            <Route path="parcheggi" element={<Parcheggi />} />
            <Route path="scadenziario" element={P(["scadenzario.view"], <Scadenziario />)} />
            <Route path="marina" element={P(["marina.view"], <Marina />)} />
            <Route path="marina/berths" element={<MarinaBerths />} />
            <Route path="marina/admin" element={<A el={<MarinaAdmin />} />} />
            <Route path="nostromo/tasks" element={P(["nostromo.tasks"], <NostromoTasks />)} />
            <Route path="admin" element={<A el={<Admin />} />} />
            <Route path="admin/users" element={<A el={<AdminUsers />} />} />
            <Route path="admin/profiles" element={<A el={<AdminProfiles />} />} />
            <Route path="admin/import" element={<A el={<AdminImport />} />} />
            <Route path="admin/luoghi" element={<A el={<Luoghi />} />} />
            <Route path="admin/users/new" element={<A el={<UserForm />} />} />
            <Route path="admin/users/:id" element={<A el={<UserForm />} />} />
            <Route path="admin/leaves" element={<A el={<AdminLeaves />} />} />
            <Route path="admin/communications" element={<A el={<AdminComms />} />} />
            <Route path="admin/tasks" element={<A el={<AdminTasks />} />} />
            <Route path="admin/shifts" element={<A el={<AdminShifts />} />} />
            <Route path="admin/shifts-view" element={<A el={<AdminShiftsView />} />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" richColors mobileOffset={{ bottom: 84 }} />
      <UpdateNotice />
      </PermProvider>
    </AuthProvider>
  );
}
